// A viewing request or a viewing, in the one lettings drawer (docs/plans/lettings-one-form.md; reworked 26 Oct 2026, later:
// "hierarchical — book it or reject it; after, mark it completed or lost"; "buttons at the bottom"; "no modal on top").
// The footer is one "Actions" button whose menu opens upwards (3 Nov 2026 — ui ActionsButton; it was a row of buttons): it
// lists only what the state allows — the next step first, the destructive ones last in red, a disabled one with its reason
// as hover text — and each item turns the drawer into that step (Back + confirm). Edit (the header's button until then)
// is in the menu too; the header keeps the status chip:
//   new request (no viewing on the go)   Book · Edit · Reject
//   booked (before or after its time)    Mark completed (the outcome → COMPLETED / NO_SHOW; disabled before the time) ·
//                                        Assign host · Reschedule · Edit ·
//                                        Cancel viewing (with "they are no longer looking — mark the request lost")
// Book and Reschedule pick from the flat's free slots (GET /units/:id/viewing-slots; docs/plans/viewing-first-onboarding.md),
// with "Other time (override)" for staff, which books outside them.
//   completed, request still open        Register for home · Edit · Mark lost
//   applied / lost                       Message them… only
// Since 3 Nov 2026 (docs/plans/viewing-first-onboarding.md) there is no Convert: nobody applies for them until the viewing
// is marked completed, and then staff (applications.create) and the agent who hosted it get the same Register for home
// ("Apply for this tenant" until 3 Nov 2026 — one label, one form) — the flat's Refer drawer on Lettings → Available,
// pre-filled from their own form (applyForTenantLink). The host agent (reservations.reserve, agentId = them) may Mark
// completed too; Assign host, Reschedule and Cancel stay staff only.
// There is no Confirm (user, 26 Oct): an older CONFIRMED viewing reads and acts as Booked. Telling them is asked after the
// one confirming button of Book, Reject, Reschedule, Cancel and Mark lost (TellButtons: Email · Text · Both · Don't tell
// them). Every button shows only to someone who may do it (useAuth can): an outside agent opening a viewing they host (the
// portal's My viewings) sees Mark completed, then Register for home (`referTo`).
// Since 3 Nov 2026 the website and the app book a viewing with a name, an email, a mobile and a slot, so the Overview shows
// only that — the tenant (first and last name, email, mobile, where they came in, the team's notes) and the viewing (home,
// when, host, status, outcome, what they said), or when suits them while nothing is booked — not the old whole application
// form. Since 3 Nov 2026, later, the tenant and the viewing are one table of label | value, two tables side by side (Drawer.tsx
// FieldTable — no headings, no rule, no space between the two groups; a blank reads "Not added"); Edit edits the same details (and the slots while nothing is booked; leads.service keeps the rest of a stored form).
// History lists their other viewings (no tab when there are none). After an action the drawer goes straight back to the
// record with a notice bar (Drawer.tsx useNotice / NoticeBar) — no "Back to the record" screen.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionsButton, Button, Field, Input, Segmented, Select, SkeletonRows, Textarea, type MenuEntry } from '../ui';
import { PhoneInput } from '../ui/PhoneInput';
import { FormError, FormGrid } from '../forms/common';
import { useAuth } from '../../stores/auth';
import { useUserOptions, useListingOptions } from '../../lib/options';
import { leadsApi, viewingsApi, viewingChip, applyForTenantLink, leadSourceLabel, VIEWING_OUTCOMES, type Lead, type ViewingOutcome, type ViewingSlot, type Viewing, type NotifyAbout } from '../../lib/lettings';
import { VIEWING_HOURS, emptyApplication, nextWeekday, slotErrors, todayIso, type ApplicationState } from '../../lib/applicationForm';
import { contactOnlyErrors } from '../../lib/applicationSteps';
import { formatPhone } from '../../lib/phone';
import { fmtDate, fmtDay, fmtWhen, londonIso, londonParts } from '../../lib/dates';
import { viewingSettingsApi, slotsByDay } from '../../lib/viewingSettings';
import { Chip } from './bits';
import { SlotsField } from './ApplicationFields';

import {
  DrawerFooter, DrawerStep, FieldTable, MessageStep, NoteField, NoticeBar, RecordDrawer, Section, SlotChip, TellButtons, channelsOf, fromChange, mergeTell, sendNote, tellNote, useNotice,
  type Notice, type Recipient, type Tell, type TellResult,
} from './Drawer';

export interface ViewingRef { kind: 'request' | 'viewing'; leadId?: string | null; viewingId?: string | null }
type Step = 'book' | 'reject' | 'reschedule' | 'host' | 'complete' | 'cancel' | 'lost' | 'edit' | 'message';

const OPEN = ['BOOKED', 'CONFIRMED'];
const CLOSED_LEAD = ['CONVERTED', 'LOST', 'APPLIED'];
const PART_HOUR: Record<string, string> = { morning: '10:00', afternoon: '14:00', evening: '17:00' };
const slotHour = (s?: ViewingSlot | null) => (s?.time && (VIEWING_HOURS as readonly string[]).includes(s.time) ? s.time : PART_HOUR[s?.part ?? ''] ?? '10:00');
/** Has the viewing's time come (15 minutes' grace)? */
const isPast = (iso: string) => new Date(iso).getTime() < Date.now() + 15 * 60_000;

/** Any day and time (London), for "Other time (override)". */
function DayHour({ day, hour, onDay, onHour }: { day: string; hour: string; onDay: (d: string) => void; onHour: (h: string) => void }) {
  return (
    <FormGrid>
      <Field label="Day"><Input type="date" min={todayIso()} value={day} onChange={(e) => onDay(e.target.value)} className="w-full" /></Field>
      <Field label="Time"><Input type="time" step={900} value={hour} onChange={(e) => onHour(e.target.value)} className="w-full" /></Field>
    </FormGrid>
  );
}

/**
 * The flat's free slots for the next four weeks (GET /units/:id/viewing-slots): a day, then a time on it. "Other time
 * (override)" opens any day and time — the booking is then sent with `override: true` (staff only).
 */
function SlotPicker({ unitId, day, hour, onDay, onHour, override, onOverride }: {
  unitId: string | null; day: string; hour: string; onDay: (d: string) => void; onHour: (h: string) => void; override: boolean; onOverride: (on: boolean) => void;
}) {
  const slots = useQuery({ queryKey: ['/units', unitId, 'viewing-slots'], queryFn: () => viewingSettingsApi.slots(unitId!, { from: todayIso(), to: addDaysIso(todayIso(), 27) }), enabled: !!unitId });
  const byDay = slotsByDay(slots.data ?? []);
  const days = [...byDay.keys()];
  const times = byDay.get(day) ?? [];
  // Land on a free slot: the asked-for one when it is free, else the first time that day, else the first free day.
  useEffect(() => {
    if (override || !slots.data) return;
    if (!byDay.has(day)) { const d = days[0]; if (d) { onDay(d); onHour(byDay.get(d)![0]!.time); } return; }
    if (!times.some((t) => t.time === hour)) onHour(times[0]!.time);
  });
  const toggle = (
    <label className="flex items-center gap-2 text-[12.5px] text-ink-700">
      <input type="checkbox" checked={override} onChange={(e) => onOverride(e.target.checked)} />
      Other time (override) — book outside the flat's free slots
    </label>
  );
  if (override) return <>{<DayHour day={day} hour={hour} onDay={onDay} onHour={onHour} />}{toggle}</>;
  if (!unitId) return <p className="text-[12.5px] text-ink-500">Choose the home first to see its free slots.</p>;
  if (slots.isLoading) return <SkeletonRows rows={2} cols={2} />;
  return (
    <>
      {days.length ? (
        <FormGrid>
          <Field label="Day"><Select value={day} onChange={(e) => onDay(e.target.value)} className="w-full">{days.map((d) => <option key={d} value={d}>{fmtDay(d)} · {byDay.get(d)!.length} free</option>)}</Select></Field>
          <Field label="Time"><Select value={hour} onChange={(e) => onHour(e.target.value)} className="w-full">{times.map((t) => <option key={t.time} value={t.time}>{t.time}{t.remaining > 1 ? ` · ${t.remaining} places` : ''}</option>)}</Select></Field>
        </FormGrid>
      ) : <p className="text-[12.5px] text-amber-700">No free slots in the next four weeks — change the flat's viewing hours on its page, or pick another time below.</p>}
      {toggle}
    </>
  );
}
const addDaysIso = (day: string, n: number) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

export function ViewingDrawer({ target, onClose, referTo }: {
  target: ViewingRef; onClose: () => void;
  /** The agent portal: where "Register for home" goes once the viewing is completed (applyForTenantLink with their free flats). */
  referTo?: string | null;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  // An outside agent (reservations.reserve, no leads.read) reads the leads of the viewings they host (GET /leads/:id).
  const canReadLead = can('leads.read') || can('reservations.reserve');
  const [picked, setViewingId] = useState<string | null>(target.viewingId ?? null);
  const pickedViewing = useQuery({ queryKey: ['/viewings', picked], queryFn: () => viewingsApi.get(picked!), enabled: !!picked });
  const ownLeadId = target.leadId ?? pickedViewing.data?.leadId ?? null;
  const lead = useQuery({ queryKey: ['/leads', ownLeadId], queryFn: () => leadsApi.get(ownLeadId!), enabled: !!ownLeadId && canReadLead, retry: false });
  const history = useQuery({ queryKey: ['/viewings', { leadId: ownLeadId }], queryFn: () => viewingsApi.forLead(ownLeadId!), enabled: !!ownLeadId && can('viewings.read') });
  const viewings = history.data ?? [];
  // Opened on the person: their viewing on the go, else — once they are past the request stage — their latest one.
  const auto = picked ? undefined : viewings.find((x) => OPEN.includes(x.status))
    ?? (lead.data && !['NEW', 'CONTACTED'].includes(lead.data.status) ? viewings.find((x) => x.status !== 'CANCELLED') : undefined);
  const viewingId = picked ?? auto?.id ?? null;
  const viewing = useQuery({ queryKey: ['/viewings', viewingId], queryFn: () => viewingsApi.get(viewingId!), enabled: !!viewingId });

  const [step, setStep] = useState<Step | null>(null);
  const [notice, setNotice] = useNotice();
  // The sections, as the sidebar inside the drawer (the application drawer's look): Overview · History (their other viewings).
  const [tab, setTab] = useState<'overview' | 'history'>('overview');
  const refresh = () => { for (const k of ['/lettings/viewings', '/leads', '/viewings', 'my-day']) void qc.invalidateQueries({ queryKey: [k] }); };
  // An action done: back to the record, with what happened as its notice bar.
  const finish = (n: Notice) => { refresh(); void qc.invalidateQueries({ queryKey: ['/units'] }); setStep(null); setNotice(n); };
  // The next action clears the notice.
  const go = (s: Step) => { setNotice(null); setStep(s); };

  const v: Viewing | undefined = viewingId ? viewing.data : undefined;
  const l: Lead | undefined = lead.data;
  const loading = (viewingId && viewing.isLoading) || (ownLeadId && lead.isLoading && canReadLead);
  const leadClosed = !!l && CLOSED_LEAD.includes(l.status);
  const past = !!v && isPast(v.scheduledAt);
  // BOOKED and (older) CONFIRMED are one state, before or after the time; COMPLETED with the request still open = happened.
  const phase: 'request' | 'booked' | 'happened' | 'closed' =
    leadClosed || (!l && !v) ? 'closed'
      : !v || ['CANCELLED', 'NO_SHOW'].includes(v.status) ? (l ? 'request' : 'closed')
        : OPEN.includes(v.status) ? 'booked'
          : v.status === 'COMPLETED' ? (l ? 'happened' : 'closed') : 'closed';
  const slots: ViewingSlot[] = (l?.application?.preferredSlots as ViewingSlot[] | undefined) ?? l?.requirements?.preferredSlots ?? [];
  const name = l?.name ?? v?.leadName ?? l?.email ?? 'Viewing';
  const homeWords = v?.listingHeadline ?? l?.listingHeadline ?? l?.propertyName ?? 'the home';
  const chip = leadClosed ? viewingChip('request', l!.status) : v ? viewingChip('viewing', v.status, v.outcome, v.scheduledAt) : viewingChip('request', l?.status ?? 'NEW');
  const recipient: Recipient = { name: l?.name ?? v?.leadName ?? null, email: l?.email ?? null, phone: l?.phone ?? null };
  const about: NotifyAbout = v ? { viewingId: v.id } : { leadId: l?.id ?? '' };
  const back = () => setStep(null);
  const others = viewings.filter((h) => h.id !== v?.id);
  const shown = others.length ? tab : 'overview';

  const canBook = can('viewings.create'), canUpdate = can('viewings.update'), canApply = can('applications.create'), canLead = can('leads.update'), canSend = can('outbox.send');
  // The outside agent who shows them round (POST /viewings/:id/feedback lets them record it — leads.service recordFeedback).
  const isHost = !!v?.agentId && v.agentId === user?.id && can('reservations.reserve');
  // Register for home (3 Nov 2026): only once the viewing is COMPLETED and the request still open. Staff go straight to
  // the viewing's own flat; the host agent to `referTo` (the flat when it is still one of their free ones).
  const applyTo = v?.status === 'COMPLETED' && phase === 'happened'
    ? (canApply ? applyForTenantLink({ leadId: v.leadId ?? l?.id, unitId: v.unitId, listingId: v.listingId }) : isHost ? referTo ?? null : null)
    : null;

  // Every action the state allows, as the one Actions menu (3 Nov 2026): the same conditions as the buttons they were.
  const items: MenuEntry[] = [
    // The next step first (what was the primary button).
    phase === 'request' && canBook && l && { label: 'Book', icon: 'CalendarPlus', onClick: () => go('book') },
    applyTo && { label: 'Register for home', icon: 'FilePlus2', onClick: () => { onClose(); navigate(applyTo); } },
    phase === 'booked' && (canUpdate || isHost) && { label: 'Mark completed', icon: 'CircleCheck', disabled: !past, title: past ? undefined : 'Once the viewing has happened', onClick: () => go('complete') },
    // Then the rest.
    phase === 'booked' && canUpdate && { label: 'Assign host', icon: 'UserRound', onClick: () => go('host') },
    phase === 'booked' && canUpdate && { label: 'Reschedule', icon: 'CalendarClock', onClick: () => go('reschedule') },
    l && canLead && !leadClosed && { label: 'Edit', icon: 'Pencil', onClick: () => go('edit') },
    phase === 'closed' && canSend && (l || v?.leadId) && { label: 'Message them…', icon: 'MessageSquare', onClick: () => go('message') },
    // The destructive ones last (ActionsButton draws a rule above them).
    phase === 'request' && canLead && l && { label: 'Reject', icon: 'Ban', danger: true, onClick: () => go('reject') },
    phase === 'booked' && canUpdate && { label: 'Cancel viewing', icon: 'CalendarX', danger: true, onClick: () => go('cancel') },
    phase === 'happened' && canLead && { label: 'Mark lost', icon: 'UserX', danger: true, onClick: () => go('lost') },
  ];
  const footer = <DrawerFooter><ActionsButton items={items} /></DrawerFooter>;

  let body: React.ReactNode;
  if (loading) body = <SkeletonRows rows={6} cols={2} />;
  else if (step === 'book' && l) body = <BookStep lead={l} slots={slots} recipient={recipient} onBack={back} onDone={(nv, result) => { setViewingId(nv.id); finish({ title: 'Viewing booked', body: <p className="text-[13px] text-ink-700">{fmtWhen(nv.scheduledAt)}{nv.agentName ? ` with ${nv.agentName}` : ''}. Confirm it with them nearer the day.</p>, result }); }} />;
  else if (step === 'reject' && l) body = <RejectStep lead={l} homeWords={homeWords} recipient={recipient} onBack={back} onDone={(result) => finish({ title: 'Request rejected', body: <p className="text-[13px] text-ink-700">It stays on the record as lost.</p>, result })} />;
  else if (step === 'reschedule' && v) body = <RescheduleStep v={v} recipient={recipient} onBack={back} onDone={(at, result) => finish({ title: 'Viewing moved', body: <p className="text-[13px] text-ink-700">Now {fmtWhen(at)} — booked, to be confirmed again.</p>, result })} />;
  else if (step === 'host' && v) body = <HostStep v={v} onBack={back} onDone={(nv) => finish({ title: nv.agentName ? `Host: ${nv.agentName}` : 'Host removed', body: <p className="text-[13px] text-ink-700">{nv.agentName ? `${nv.agentName} shows them round on ${fmtWhen(nv.scheduledAt)}.` : 'Nobody is assigned to show them round yet.'}</p> })} />;
  else if (step === 'complete' && v) body = <CompleteStep v={v} onBack={back} onDone={(nv) => finish({ title: nv.status === 'NO_SHOW' ? 'Marked as a no-show' : 'Viewing completed', body: <p className="text-[13px] text-ink-700">{nv.status === 'NO_SHOW' ? 'They did not come. Book another time or mark the request lost.' : `Outcome: ${nv.outcomeLabel ?? '—'}. They can apply for the flat themselves — or you register them with Register for home.`}</p> })} />;
  else if (step === 'cancel' && v) body = <CancelStep v={v} lead={l} recipient={recipient} onBack={back} onDone={(result, lost) => finish({ title: lost ? 'Viewing cancelled — request lost' : 'Viewing cancelled', body: <p className="text-[13px] text-ink-700">{lost ? 'It stays on the record as lost.' : 'The request is open again — book another time or reject it.'}</p>, result })} />;
  else if (step === 'lost' && l) body = <LostStep lead={l} v={v} homeWords={homeWords} recipient={recipient} onBack={back} onDone={(result) => finish({ title: 'Marked lost', result })} />;
  else if (step === 'edit' && l) body = <EditStep lead={l} slots={slots} hideSlots={!!v} onBack={back} onDone={() => finish({ title: 'Details saved' })} />;
  else if (step === 'message' && (l || v)) body = <MessageStep about={about} recipient={recipient} templates={v ? ['viewing_confirmation'] : []} onBack={back} onSent={(result) => finish({ title: 'Message sent', result })} />;
  else body = (
    <div>
      {notice && <NoticeBar notice={notice} onClose={() => setNotice(null)} />}
      {shown === 'overview' && <>
        {!l && v && <p className="mb-2 text-[12.5px] text-ink-400">{ownLeadId ? 'You cannot see viewing requests.' : 'Booked without a request.'}</p>}
        <FieldTable items={[
          ...(l ? [
            { label: 'First name', value: l.firstName ?? (l.lastName ? null : l.name) }, { label: 'Last name', value: l.lastName },
            { label: 'Email', value: l.email }, { label: 'Mobile', value: formatPhone(l.phone) },
            { label: 'Came in by', value: leadSourceLabel(l.source) },
            { label: 'Notes', value: l.notes, wide: true },
          ] : []),
          ...(v ? [
            { label: 'Home', value: [v.unitReference, v.listingHeadline, v.propertyName].filter(Boolean).join(' · ') },
            { label: 'When', value: fmtWhen(v.scheduledAt) },
            { label: 'Shown round by', value: v.agentName },
            { label: 'Status', value: (() => { const c = viewingChip('viewing', v.status, v.outcome, v.scheduledAt); return <Chip label={c.label} colour={c.colour} />; })() },
            { label: 'Outcome', value: v.outcomeLabel },
            { label: 'What they said', value: v.feedback, wide: true },
          ] : l ? [
            { label: 'Home', value: [l.listingHeadline, l.propertyName].filter(Boolean).join(' · ') },
            { label: 'When suits them', value: slots.length ? <span className="flex flex-wrap gap-1">{slots.map((sl) => <SlotChip key={`${sl.date}${sl.time ?? sl.part}`} slot={sl} />)}</span> : null },
          ] : []),
        ]} />
      </>}

      {shown === 'history' && (
        <Section title="Other viewings">
          <ul className="divide-y divide-ink-100">
            {others.map((h) => { const c = viewingChip('viewing', h.status, h.outcome, h.scheduledAt); return (
              <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[12.5px]">
                <button type="button" onClick={() => { setViewingId(h.id); setTab('overview'); }} className="min-w-0 text-left hover:underline">
                  <b className="text-ink-900">{fmtWhen(h.scheduledAt)}</b><span className="text-ink-500"> · {[h.unitReference, h.propertyName].filter(Boolean).join(', ')}{h.agentName ? ` · with ${h.agentName}` : ''}{h.feedback ? ` · ${h.feedback.slice(0, 60)}` : ''}</span>
                </button>
                <Chip label={c.label} colour={c.colour} />
              </li>); })}
          </ul>
        </Section>
      )}
      {footer}
    </div>
  );

  return (
    <RecordDrawer open onClose={onClose} size="xl"
      eyebrow={v ? `Viewing${v.unitReference ? ` · ${v.unitReference}` : ''}` : `Viewing request${l ? ` · came in ${fmtDate(l.createdAt)}` : ''}`}
      title={name} status={<Chip label={chip.label} colour={chip.colour} />}
      nav={step || !others.length ? undefined : { value: shown, onChange: (k) => setTab(k as typeof tab), items: [
        { key: 'overview', label: 'Overview' },
        { key: 'history', label: 'History', count: others.length },
      ] }}>
      {body}
    </RecordDrawer>
  );
}

// ── The steps ───────────────────────────────────────────────────────────────

function BookStep({ lead, slots, recipient, onBack, onDone }: { lead: Lead; slots: ViewingSlot[]; recipient: Recipient; onBack: () => void; onDone: (v: Viewing, r: TellResult | null) => void }) {
  const first = slots.find((s) => s.date >= todayIso());
  const me = useAuth((s) => s.user);
  const [day, setDay] = useState(first?.date ?? nextWeekday(1));
  const [hour, setHour] = useState(slotHour(first));
  const [agentId, setAgentId] = useState(lead.assignedTo ?? me?.id ?? '');
  const [listingId, setListingId] = useState(lead.listingId ?? '');
  const [override, setOverride] = useState(false);
  const canUsers = useAuth((s) => s.can('users.read'));
  const users = useUserOptions(canUsers);
  const listings = useListingOptions(!lead.listingId);
  const unitId: string | null = lead.listingId && listingId === lead.listingId ? lead.listingUnitId ?? null : ((listings.data ?? []) as Array<{ id: string; unitId?: string | null }>).find((x) => x.id === listingId)?.unitId ?? null;
  const book = useMutation({
    mutationFn: async (tell: Tell) => {
      const ch = channelsOf(tell);
      const nv = await viewingsApi.book({ listingId, leadId: lead.id, scheduledAt: londonIso(day, hour), agentId: agentId || null, sendConfirmation: ch.includes('EMAIL'), ...(override ? { override: true } : {}) });
      const result = ch.includes('SMS') ? await sendNote({ viewingId: nv.id }, 'SMS', '', 'viewing_confirmation') : null;
      return { nv, result };
    },
    onSuccess: ({ nv, result }) => onDone(nv, result),
  });
  return (
    <DrawerStep title={`Book the viewing${day ? ` — ${fmtDay(day)} ${hour}` : ''}`} onBack={onBack}
      intro={slots.length > 0 ? <span className="flex flex-wrap items-center gap-1">They asked for {slots.map((s) => <button key={`${s.date}${s.time ?? s.part}`} type="button" onClick={() => { setDay(s.date); setHour(slotHour(s)); }} className="hover:opacity-80"><SlotChip slot={s} /></button>)}</span> : 'They did not ask for a time.'}
      actions={<TellButtons verb="Book" recipient={recipient}
        onGo={(t) => book.mutate(t)} pending={book.isPending} disabled={!listingId || !day || !hour} />}>
      {!lead.listingId && <Field label="Home"><Select value={listingId} onChange={(e) => setListingId(e.target.value)} className="w-full"><option value="">Choose a listing…</option>{(listings.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.headline}{x.property?.name ? ` · ${x.property.name}` : ''}</option>)}</Select></Field>}
      <SlotPicker unitId={unitId} day={day} hour={hour} onDay={setDay} onHour={setHour} override={override} onOverride={setOverride} />
      <Field label="Shown round by">
        <Select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="w-full">
          <option value="">Unassigned</option>
          {canUsers ? (users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>) : me && <option value={me.id}>{me.firstName} {me.lastName}</option>}
        </Select>
      </Field>
      <FormError error={book.error} />
    </DrawerStep>
  );
}

function RejectStep({ lead, homeWords, recipient, onBack, onDone }: { lead: Lead; homeWords: string; recipient: Recipient; onBack: () => void; onDone: (r: TellResult | null) => void }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState(`Thank you for asking to see ${homeWords}. We're sorry — we can't offer you a viewing this time. We'd be glad to help you find another home with us.`);
  const go = useMutation({
    mutationFn: async (tell: Tell) => {
      await leadsApi.update(lead.id, { status: 'LOST', lostReason: reason.trim() });
      return tellNote({ leadId: lead.id }, tell, note);
    },
    onSuccess: onDone,
  });
  return (
    <DrawerStep title="Reject this request" intro="It stays on the record as lost, so the numbers stay honest." onBack={onBack}
      actions={<TellButtons verb="Reject" danger recipient={recipient} onGo={(t) => go.mutate(t)} pending={go.isPending} disabled={!reason.trim() || (!note.trim())} />}>
      <Field label="Why (for the team) *"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Flat taken, budget, timing…" className="w-full" maxLength={250} /></Field>
      <NoteField value={note} onChange={setNote} />
      <FormError error={go.error} />
    </DrawerStep>
  );
}

function RescheduleStep({ v, recipient, onBack, onDone }: { v: Viewing; recipient: Recipient; onBack: () => void; onDone: (at: string, r: TellResult | null) => void }) {
  const at = londonParts(v.scheduledAt);
  const [day, setDay] = useState(at.day >= todayIso() ? at.day : nextWeekday(1));
  const [hour, setHour] = useState(at.time);
  const [override, setOverride] = useState(false);
  const [note, setNote] = useState('');
  const go = useMutation({
    mutationFn: async (tell: Tell) => {
      const ch = channelsOf(tell);
      const r = await viewingsApi.reschedule(v.id, londonIso(day, hour), ch[0] ? { channel: ch[0], message: note.trim() || null } : null, override);
      return { at: r.scheduledAt, result: mergeTell(fromChange(r), ch[1] ? await sendNote({ viewingId: v.id }, ch[1], note, note.trim() ? undefined : 'viewing_confirmation') : null) };
    },
    onSuccess: ({ at, result }) => onDone(at, result),
  });
  return (
    <DrawerStep title={`Move the viewing${day ? ` to ${fmtDay(day)} ${hour}` : ''}`} intro={`Now ${fmtWhen(v.scheduledAt)}. A moved viewing goes back to Booked until it is confirmed again.`} onBack={onBack}
      actions={<TellButtons verb="Move" recipient={recipient} onGo={(t) => go.mutate(t)} pending={go.isPending} disabled={!day || !hour} />}>
      <SlotPicker unitId={v.unitId} day={day} hour={hour} onDay={setDay} onHour={setHour} override={override} onOverride={setOverride} />
      <NoteField value={note} onChange={setNote} label="Your own words (optional)" hint="Leave empty to send the standard viewing confirmation with the new time." />
      <FormError error={go.error} />
    </DrawerStep>
  );
}

/** Assign host: who shows them round (PATCH /viewings/:id { agentId }) — the staff list, as Book's "Shown round by". */
function HostStep({ v, onBack, onDone }: { v: Viewing; onBack: () => void; onDone: (v: Viewing) => void }) {
  const me = useAuth((s) => s.user);
  const canUsers = useAuth((s) => s.can('users.read'));
  const users = useUserOptions(canUsers);
  const [agentId, setAgentId] = useState(v.agentId ?? me?.id ?? '');
  const go = useMutation({ mutationFn: () => viewingsApi.assign(v.id, agentId || null), onSuccess: onDone });
  return (
    <DrawerStep title="Assign host" intro={`${fmtWhen(v.scheduledAt)}${v.agentName ? ` — now ${v.agentName}` : ' — nobody assigned yet'}.`} onBack={onBack}
      actions={<Button onClick={() => go.mutate()} disabled={go.isPending || (agentId || null) === (v.agentId ?? null)}>{go.isPending ? 'Saving…' : 'Assign'}</Button>}>
      <Field label="Shown round by">
        <Select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="w-full">
          <option value="">Unassigned</option>
          {canUsers ? (users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)
            : <>{me && <option value={me.id}>{me.firstName} {me.lastName}</option>}{v.agentId && v.agentId !== me?.id && <option value={v.agentId}>{v.agentName ?? 'Current host'}</option>}</>}
        </Select>
      </Field>
      <FormError error={go.error} />
    </DrawerStep>
  );
}

/** Mark completed: what came of it (POST /viewings/:id/feedback) — Keen / Thinking about it / Not for them → COMPLETED, No show → NO_SHOW. */
function CompleteStep({ v, onBack, onDone }: { v: Viewing; onBack: () => void; onDone: (v: Viewing) => void }) {
  const [outcome, setOutcome] = useState<ViewingOutcome>('LIKED');
  const [note, setNote] = useState(v.feedback ?? '');
  const go = useMutation({ mutationFn: () => viewingsApi.feedback(v.id, { outcome, feedback: note.trim() || null }), onSuccess: onDone });
  return (
    <DrawerStep title="Mark completed" intro={`The viewing on ${fmtWhen(v.scheduledAt)}. Once completed, they can apply for the flat themselves — or you register them with Register for home.`} onBack={onBack}
      actions={<Button onClick={() => go.mutate()} disabled={go.isPending}>{go.isPending ? 'Saving…' : outcome === 'NO_SHOW' ? 'Mark as no-show' : 'Mark completed'}</Button>}>
      <Field label="How did it go?">
        <Segmented items={VIEWING_OUTCOMES.map((o) => ({ key: o.key, label: o.label }))} value={outcome} onChange={setOutcome} />
      </Field>
      <Field label="Note (optional)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Loved the light, asked about parking…" className="w-full" /></Field>
      <FormError error={go.error} />
    </DrawerStep>
  );
}

function CancelStep({ v, lead, recipient, onBack, onDone }: { v: Viewing; lead?: Lead; recipient: Recipient; onBack: () => void; onDone: (r: TellResult | null, lost: boolean) => void }) {
  const canLead = useAuth((s) => s.can('leads.update'));
  const [reason, setReason] = useState('');
  const [lost, setLost] = useState(false);
  const [note, setNote] = useState(`We're sorry — we've had to cancel your viewing on ${fmtWhen(v.scheduledAt)}. We'll be in touch to find another time.`);
  const go = useMutation({
    mutationFn: async (tell: Tell) => {
      await viewingsApi.cancel(v.id, reason.trim() || null);
      // The secondary choice: they have stopped looking, so the request closes as lost (was the footer's Mark lost).
      if (lost && lead) await leadsApi.update(lead.id, { status: 'LOST', lostReason: (reason.trim() || 'No longer looking').slice(0, 250) });
      return { result: await tellNote({ viewingId: v.id }, tell, note), lost };
    },
    onSuccess: ({ result, lost: l }) => onDone(result, l),
  });
  const flip = (on: boolean) => {
    setLost(on);
    setNote(on ? 'Thank you for letting us know — we have cancelled your viewing. We\'d be glad to help if you start looking again.' : `We're sorry — we've had to cancel your viewing on ${fmtWhen(v.scheduledAt)}. We'll be in touch to find another time.`);
  };
  return (
    <DrawerStep title="Cancel this viewing" intro={lost ? 'The viewing is cancelled and the request closes as lost.' : 'It stays on the record as cancelled; the request opens again to book another time.'} onBack={onBack}
      actions={<TellButtons verb={lost ? 'Cancel · mark lost' : 'Cancel viewing'} danger recipient={recipient} onGo={(t) => go.mutate(t)} pending={go.isPending} disabled={!note.trim()} />}>
      <Field label="Why (for the team)"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full" maxLength={250} /></Field>
      {lead && canLead && <label className="flex items-center gap-2 text-[12.5px] text-ink-700"><input type="checkbox" checked={lost} onChange={(e) => flip(e.target.checked)} />They are no longer looking — mark the request lost</label>}
      <NoteField value={note} onChange={setNote} />
      <FormError error={go.error} />
    </DrawerStep>
  );
}

const LOST_OUTCOMES = VIEWING_OUTCOMES.filter((o) => o.key !== 'LIKED');

function LostStep({ lead, v, homeWords, recipient, onBack, onDone }: { lead: Lead; v?: Viewing; homeWords: string; recipient: Recipient; onBack: () => void; onDone: (r: TellResult | null) => void }) {
  const writable = !!v && !v.outcome && isPast(v.scheduledAt) && v.status !== 'CANCELLED';
  const [outcome, setOutcome] = useState<ViewingOutcome>((v?.outcome && v.outcome !== 'LIKED' ? v.outcome : 'NOT_FOR_THEM') as ViewingOutcome);
  const [feedback, setFeedback] = useState(v?.feedback ?? '');
  const [note, setNote] = useState(`Thank you for coming to see ${homeWords}. We understand it wasn't the one for you — we'd be glad to help you find another home with us.`);
  const go = useMutation({
    mutationFn: async (tell: Tell) => {
      if (writable) await viewingsApi.feedback(v!.id, { outcome, feedback: feedback.trim() || null });
      const label = LOST_OUTCOMES.find((o) => o.key === outcome)?.label ?? 'Lost';
      await leadsApi.update(lead.id, { status: 'LOST', lostReason: [label, feedback.trim()].filter(Boolean).join(' — ').slice(0, 250) });
      return tellNote({ leadId: lead.id }, tell, note);
    },
    onSuccess: onDone,
  });
  return (
    <DrawerStep title="Mark lost" intro="It stays on the record as lost." onBack={onBack}
      actions={<TellButtons verb="Mark lost" danger recipient={recipient} onGo={(t) => go.mutate(t)} pending={go.isPending} disabled={!note.trim()} />}>
      {writable || !v ? (
        <Field label="What came of it">
          <Segmented items={LOST_OUTCOMES.map((o) => ({ key: o.key, label: o.label }))} value={outcome} onChange={setOutcome} />
        </Field>
      ) : null}
      <Field label="What they said"><Textarea rows={2} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Second bedroom too small, wants parking…" className="w-full" /></Field>
      <NoteField value={note} onChange={setNote} />
      <FormError error={go.error} />
    </DrawerStep>
  );
}

/**
 * Edit the request (3 Nov 2026): the same details the Overview shows — first and last name, email, mobile (the rules of the
 * website's contact step, contactOnlyErrors), When suits them while nothing is booked, and the team's notes. Only these go
 * (PATCH /leads/:id without a form): leads.service keeps the rest of a stored form and moves its contact details along.
 */
function EditStep({ lead, slots, hideSlots, onBack, onDone }: { lead: Lead; slots: ViewingSlot[]; hideSlots: boolean; onBack: () => void; onDone: () => void }) {
  const [s, setS] = useState<ApplicationState>(() => {
    // An older lead may hold only the whole name.
    const [first = '', ...rest] = lead.firstName || lead.lastName ? [] : (lead.name ?? '').trim().split(/\s+/);
    return emptyApplication({
      firstName: lead.firstName ?? first, lastName: lead.lastName ?? rest.join(' '), email: lead.email ?? '', mobile: lead.phone ?? '',
      preferredSlots: slots.filter((x) => x.date && x.time).map((x) => ({ date: x.date, time: x.time! })),
    });
  });
  const set = (p: Partial<ApplicationState>) => setS((cur) => ({ ...cur, ...p }));
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [tried, setTried] = useState(false);
  const errs: Record<string, string | undefined> = { ...contactOnlyErrors(s), ...(hideSlots ? {} : { preferredSlots: slotErrors(s).preferredSlots }) };
  const err = (k: string) => (tried ? errs[k] : undefined);
  const save = useMutation({
    mutationFn: () => leadsApi.update(lead.id, {
      firstName: s.firstName.trim(), lastName: s.lastName.trim(), email: s.email.trim(), phone: s.mobile, notes: notes.trim() || null,
      ...(hideSlots ? {} : { preferredSlots: s.preferredSlots }),
    }),
    onSuccess: onDone,
  });
  const submit = () => { setTried(true); if (!Object.values(errs).some(Boolean)) save.mutate(); };
  const line = (k: string) => (err(k) ? <span className="mt-1 block text-[11px] text-red-600">{err(k)}</span> : null);
  return (
    <DrawerStep title="Edit the request" onBack={onBack}
      actions={<Button onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>}>
      <FormGrid>
        <div><Field label="First name *"><Input value={s.firstName} autoComplete="off" onChange={(e) => set({ firstName: e.target.value })} className="w-full" /></Field>{line('firstName')}</div>
        <div><Field label="Last name *"><Input value={s.lastName} autoComplete="off" onChange={(e) => set({ lastName: e.target.value })} className="w-full" /></Field>{line('lastName')}</div>
        <div><Field label="Email *"><Input type="email" value={s.email} autoComplete="off" onChange={(e) => set({ email: e.target.value })} className="w-full" /></Field>{line('email')}</div>
        <div><Field label="Mobile *"><PhoneInput value={s.mobile} autoComplete="off" onChange={(mobile) => set({ mobile })} /></Field>{line('mobile')}</div>
      </FormGrid>
      {!hideSlots && <SlotsField s={s} set={set} error={err('preferredSlots')} />}
      <Field label="Notes (the team's)"><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the team should know" className="w-full" maxLength={4000} /></Field>
      <FormError error={save.error} />
    </DrawerStep>
  );
}
